import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./product.css";
import Loader2 from "../../components/Loader2";
import { getProductById } from "../../services/auth/User";
import { getCartItems, setCartItems } from "../../util/Cart";
import toast from "react-hot-toast";

function Product() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [productData, setProductData] = useState(null);
  const [isPending, setIsPending] = useState(true);
  const [currentImage, setCurrentImage] = useState("");
  const [selectedOptions, setSelectedOptions] = useState({});

  const fetchProductData = async (productId) => {
    try {
        const response = await getProductById(productId);
        setProductData(response);
        setCurrentImage(response.imageSrc[0] || "https://www.vallabhcomponent.com/assets/images/default.jpg");

        const initialOptions = {};
        response.options.forEach(option => {
            if (option.optionName.trim() !== "") {  // Filter out options with empty names
                const validOptions = option.optionValue.filter(value => value.trim() !== "");
                initialOptions[option.optionName] = validOptions.length === 1 ? validOptions[0] : "";
            }
        });
        setSelectedOptions(initialOptions);
    } catch (error) {
        console.error("Failed to fetch product data:", error);
    } finally {
        setIsPending(false);
    }
};


  useEffect(() => {
    fetchProductData(id);
  }, [id]);

  if (isPending) {
    return <Loader2 />;
  }

  if (!productData) {
    return <div>Product not found</div>;
  }

  const handleImageClick = (image) => {
    setCurrentImage(image);
  };

  const calculateOfferPercentage = (originalPrice, discountedPrice) => {
    if (!originalPrice || !discountedPrice) return 0;
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
  };

  const offerPercentage = calculateOfferPercentage(productData.variantCompareAtPrice, productData.variantPrice);

  const handleOptionChange = (optionName, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionName]: value
    }));
  };

  const handleAddToCart = () => {
    const allOptionsSelected = Object.values(selectedOptions).every(value => value !== "");
    if (!allOptionsSelected) {
        toast.error("Please select all options before adding to cart");
        return;
    }

    const optionValuesArray = Object.values(selectedOptions);
    const currentCartItems = getCartItems();
    const existingItemIndex = currentCartItems.findIndex(item => item.product._id === productData._id);

    if (existingItemIndex !== -1) {
        currentCartItems[existingItemIndex].quantity += 1;
        currentCartItems[existingItemIndex].options = optionValuesArray;  // Store options as an array
    } else {
        currentCartItems.push({ product: productData, quantity: 1, options: optionValuesArray }); // Store options as an array
    }

    setCartItems(currentCartItems);
    toast.success("Product added to cart with selected options");
};

const handleCheckout = () => {
    // Check if all selectable options are chosen (those with more than one valid value)
    const userData = localStorage.getItem('userData');
    if (!userData) {
        // If user data is not present, save last path and redirect to login
        localStorage.setItem('lastPath', window.location.pathname);
        navigate('/login');
        return; // Prevent further actions and redirect to login
    }

    
    const allOptionsSelected = Object.values(selectedOptions).every(value => value !== "");
    if (!allOptionsSelected) {
        toast.error("Please select all options before proceeding to checkout");
        return; // Prevent navigation if not all options are selected
    }

    const optionValuesArray = Object.values(selectedOptions); // Extract values as an array
    // Proceed to navigate with the selected option values included in the state
    navigate(`/checkout/${productData._id}`, { state: { options: optionValuesArray, productDetails: productData } });
};



const renderProductOptions = () => {
  return productData.options
      .filter(option => option.optionName.trim() !== "")  // Only consider options with non-empty names
      .map((option, index) => {
          const validValues = option.optionValue.filter(value => value.trim() !== "");
          if (validValues.length === 1) {
              return (
                  <div key={index} className="product-dropdown">
                      <label>{option.optionName}: {validValues[0]}</label>
                  </div>
              );
          } else {
              return (
                  <div key={index} className="product-dropdown">
                      <label>{option.optionName}: </label>
                      <select
                          value={selectedOptions[option.optionName]}
                          onChange={e => handleOptionChange(option.optionName, e.target.value)}
                          required
                      >
                          <option value="">Select {option.optionName}</option>
                          {validValues.map((value, idx) => (
                              <option key={idx} value={value}>{value}</option>
                          ))}
                      </select>
                  </div>
              );
          }
      });
};


  return (
    <div className="product">
      <div className="product-inner">
        <div className="product-hero">
          <div className="ph-left">
            <div className="ph-left-img">
              <img
                src={currentImage}
                alt={productData.imageAltText[productData.imageSrc.indexOf(currentImage)] || "Product"}
              />
            </div>
            <div className="ph-left-slider">
              {productData.imageSrc.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={productData.imageAltText[index] || `Product ${index + 1}`}
                  onClick={() => handleImageClick(image)}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </div>
          </div>
          <div className="ph-right">
            <div className="ph-right-top">
              <h1 className="phr-h1">{productData.title}</h1>
              <p className="price">
                Rs {productData.variantPrice}
                <span> Rs {productData.variantCompareAtPrice}</span>
                <span>{offerPercentage}% off</span>
              </p>
              <div className="btn-grp-product">
                <button className="btn-p2" onClick={handleCheckout}>Buy Now</button>
                <button className="btn-p1" onClick={handleAddToCart}>Add to Cart</button>
              </div>
            </div>
            <div className="ph-right-middle">
              {renderProductOptions()}
            </div>
            <div className="ph-right-bottom">
              <div dangerouslySetInnerHTML={{ __html: productData.bodyHtml }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Product;
